import { formatLocalDate } from '../utils/localDateTime';
import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import {
    collection, query, where, onSnapshot, orderBy,
    getDoc, doc, updateDoc
} from 'firebase/firestore';
import type { Organization, AgencyClient, UserProfile } from '@types';

interface ClientDetailPageProps {
    clientId: string;
    currentOrganization: Organization;
    currentUser: UserProfile;
    onNavigate: (nav: { page: any; params?: any }) => void;
    onActivateClient: (client: AgencyClient) => void;
    onBack: () => void;
}

const RISK_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    Low: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
    Medium: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
    High: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' },
    Critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' },
};

const STAT_CARD = ({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) => (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex items-center gap-4">
        <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-400 shrink-0">
            {icon}
        </div>
        <div>
            <p className="text-2xl font-black text-white">{value}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black">{label}</p>
        </div>
    </div>
);

const ClientDetailPage: React.FC<ClientDetailPageProps> = ({
    clientId, currentOrganization, currentUser, onNavigate, onActivateClient, onBack
}) => {
    const [client, setClient] = useState<AgencyClient | null>(null);
    const [reports, setReports] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'reports' | 'projects'>('overview');

    // Subscribe to client document
    useEffect(() => {
        if (!db || !clientId) return;
        const ref = doc(db, 'clients', clientId);
        const unsub = onSnapshot(ref, snap => {
            if (snap.exists()) {
                setClient({ ...snap.data(), id: snap.id } as AgencyClient);
            }
            setIsLoading(false);
        });
        return () => unsub();
    }, [clientId]);

    // Subscribe to saved reports for this client
    useEffect(() => {
        if (!db || !clientId) return;
        const q = query(
            collection(db, 'saved_intel'),
            where('organizationId', '==', currentOrganization.id),
            where('clientId', '==', clientId),
            orderBy('savedAt', 'desc')
        );
        const unsub = onSnapshot(q, snap => {
            setReports(snap.docs.map(d => ({ ...d.data(), id: d.id })));
        }, err => console.error('Client reports error:', err));
        return () => unsub();
    }, [clientId, currentOrganization.id]);

    // Subscribe to SRM projects for this client
    useEffect(() => {
        if (!db || !clientId) return;
        const q = query(
            collection(db, 'srm_projects'),
            where('organizationId', '==', currentOrganization.id),
            where('clientId', '==', clientId),
            orderBy('createdAt', 'desc')
        );
        const unsub = onSnapshot(q, snap => {
            setProjects(snap.docs.map(d => ({ ...d.data(), id: d.id })));
        }, err => console.error('Client projects error:', err));
        return () => unsub();
    }, [clientId, currentOrganization.id]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
            </div>
        );
    }

    if (!client) {
        return (
            <div className="text-center py-24">
                <p className="text-slate-400">Client programme not found.</p>
                <button onClick={onBack} className="mt-4 text-blue-400 text-sm hover:underline">← Back to Agency Dashboard</button>
            </div>
        );
    }

    const risk = RISK_COLORS[client.riskProfile ?? 'Medium'];

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-slate-500">
                <button onClick={onBack} className="hover:text-slate-300 transition-colors flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Agency Dashboard
                </button>
                <span>/</span>
                <span className="text-white font-bold">{client.name}</span>
            </div>

            {/* Client Header Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none" />
                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        {/* Left: Identity */}
                        <div className="flex items-start gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-blue-600/20 flex items-center justify-center font-black text-blue-400 text-3xl border border-blue-500/30 shadow-lg shadow-blue-500/10">
                                {client.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-[0.4em] mb-1">Client Programme</p>
                                <h1 className="text-3xl font-black text-white">{client.name}</h1>
                                <div className="flex flex-wrap items-center gap-3 mt-2">
                                    {client.industry && (
                                        <span className="text-[10px] font-black text-slate-400 bg-slate-800 px-2.5 py-1 rounded border border-slate-700 uppercase tracking-widest">
                                            {client.industry}
                                        </span>
                                    )}
                                    <span className={`text-[10px] font-black px-2.5 py-1 rounded border uppercase tracking-widest ${risk.text} ${risk.bg} ${risk.border}`}>
                                        {client.riskProfile} Risk
                                    </span>
                                    <span className={`text-[10px] font-black px-2.5 py-1 rounded border uppercase tracking-widest ${client.status === 'Active' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : 'text-slate-400 bg-slate-800 border-slate-700'}`}>
                                        {client.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex gap-3 shrink-0">
                            <button
                                onClick={() => onActivateClient(client)}
                                className="px-5 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M8.464 15.536a5 5 0 010-7.072m7.072 0a5 5 0 010 7.072M12 12h.01" />
                                </svg>
                                Activate Context
                            </button>
                        </div>
                    </div>

                    {/* Contact info */}
                    {(client.contactName || client.contactEmail) && (
                        <div className="mt-4 pt-4 border-t border-slate-800 flex items-center gap-6 text-sm text-slate-400">
                            {client.contactName && (
                                <span className="flex items-center gap-1.5">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    {client.contactName}
                                </span>
                            )}
                            {client.contactEmail && (
                                <a href={`mailto:${client.contactEmail}`} className="flex items-center gap-1.5 hover:text-blue-400 transition-colors">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {client.contactEmail}
                                </a>
                            )}
                            {(client.operatingCountries?.length ?? 0) > 0 && (
                                <span className="flex items-center gap-1.5">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {client.operatingCountries!.join(', ')}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <STAT_CARD
                    label="Saved Reports"
                    value={reports.length}
                    icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                />
                <STAT_CARD
                    label="SRM Projects"
                    value={projects.length}
                    icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0h10a2 2 0 012 2v2M7 7h10" /></svg>}
                />
                <STAT_CARD
                    label="Active Since"
                    value={formatLocalDate(client.createdAt)}
                    icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
                />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 w-fit">
                {(['overview', 'reports', 'projects'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all capitalize ${activeTab === tab ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-400 hover:text-white'}`}
                    >
                        {tab === 'projects' ? `Projects (${projects.length})` : tab}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Quick Actions</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { label: 'Threat Analysis', icon: '🎯', page: 'suiteDashboard', suiteName: 'Threat Analysis Hub' },
                                { label: 'Country Intel', icon: '🌍', page: 'gsocDashboard', suiteName: undefined },
                                { label: 'Geopolitical Risk', icon: '⚡', page: 'dashboard', industry: 'GeoPolitical Risk Management and Intelligence' },
                                { label: 'Executive Report', icon: '📋', page: 'executiveReport', suiteName: undefined },
                            ].map(action => (
                                <button
                                    key={action.label}
                                    onClick={() => {
                                        // Activate client context first, then navigate
                                        onActivateClient(client);
                                        onNavigate({
                                            page: action.page,
                                            params: {
                                                clientId: client.id,
                                                clientName: client.name,
                                                activeClientIndustry: client.industry,
                                                suiteName: action.suiteName,
                                                industry: (action as any).industry,
                                                purpose: 'agency',
                                            }
                                        });
                                    }}
                                    className="flex flex-col items-center justify-center gap-2 p-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500/40 rounded-xl text-center transition-all group"
                                >
                                    <span className="text-2xl group-hover:scale-110 transition-transform">{action.icon}</span>
                                    <span className="text-[10px] font-black text-slate-400 group-hover:text-white uppercase tracking-widest">{action.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Recent Intelligence</h3>
                        {reports.length === 0 ? (
                            <div className="text-center py-8 text-slate-600">
                                <p className="text-sm">No intelligence reports saved for this client yet.</p>
                                <p className="text-xs mt-1">Activate this client context, then run analyses and save results.</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {reports.slice(0, 5).map(r => (
                                    <div key={r.id} className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
                                        <div>
                                            <p className="text-sm font-bold text-white">{r.title || r.reportType || 'Intelligence Report'}</p>
                                            <p className="text-xs text-slate-500 mt-0.5">{r.savedAt ? formatLocalDate(r.savedAt.toDate?.() ?? r.savedAt) : 'Unknown date'}</p>
                                        </div>
                                        <span className="text-[9px] font-black text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/30 uppercase tracking-widest">
                                            {r.reportType || 'Report'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'reports' && (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">All Saved Intelligence ({reports.length})</h3>
                    {reports.length === 0 ? (
                        <div className="text-center py-12 text-slate-600">
                            <p className="text-sm">No reports saved under this client programme yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {reports.map(r => (
                                <div key={r.id} className="flex items-center justify-between p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-blue-500/30 transition-colors">
                                    <div>
                                        <p className="text-sm font-bold text-white">{r.title || r.reportType || 'Intelligence Report'}</p>
                                        <div className="flex items-center gap-3 mt-1">
                                            <span className="text-[9px] font-black text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/30 uppercase tracking-widest">
                                                {r.reportType || 'Report'}
                                            </span>
                                            <span className="text-[10px] text-slate-500">{r.savedAt ? formatLocalDate(r.savedAt.toDate?.() ?? r.savedAt) : '—'}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'projects' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">SRM Projects ({projects.length})</h3>
                        <button
                            onClick={() => {
                                onActivateClient(client!);
                                onNavigate({ page: 'srm_command_center', params: { clientId: client!.id, clientName: client!.name, purpose: 'agency' } });
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-blue-500/20"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                            New Project for Client
                        </button>
                    </div>

                    {projects.length === 0 ? (
                        <div className="bg-slate-900 border-2 border-dashed border-slate-800 rounded-2xl p-10 text-center">
                            <div className="w-14 h-14 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-600">
                                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0h10a2 2 0 012 2v2M7 7h10" /></svg>
                            </div>
                            <p className="text-slate-400 font-medium">No SRM projects for this client yet.</p>
                            <p className="text-slate-600 text-sm mt-1">Activate this client context, then create a project in the SRM Command Center.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {projects.map(proj => {
                                const totalMods = (proj.modules || []).length;
                                const readyMods = (proj.modules || []).filter((m: any) => m.status === 'Ready').length;
                                const pct = totalMods > 0 ? Math.round((readyMods / totalMods) * 100) : 0;
                                const STATUS_COLOR: Record<string, string> = {
                                    'Active': 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
                                    'Review': 'text-amber-400 bg-amber-500/10 border-amber-500/30',
                                    'Complete': 'text-blue-400 bg-blue-500/10 border-blue-500/30',
                                };
                                const statusClass = STATUS_COLOR[proj.status] || 'text-slate-400 bg-slate-800 border-slate-700';
                                return (
                                    <div key={proj.id} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 hover:border-blue-500/40 transition-all group">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="min-w-0">
                                                <h4 className="font-black text-white text-sm leading-tight group-hover:text-blue-300 transition-colors truncate">
                                                    {proj.name || proj.projectName || 'Untitled Project'}
                                                </h4>
                                                <p className="text-[10px] text-slate-500 mt-0.5 font-mono">{proj.projectNumber || proj.id?.slice(0, 8)?.toUpperCase()}</p>
                                            </div>
                                            <span className={`shrink-0 text-[9px] font-black px-2 py-0.5 rounded border uppercase tracking-widest ml-3 ${statusClass}`}>
                                                {proj.status || 'Active'}
                                            </span>
                                        </div>

                                        {/* Module Progress Bar */}
                                        <div className="mb-4">
                                            <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                                                <span className="font-bold uppercase tracking-widest">Module Completion</span>
                                                <span className="font-black text-white">{pct}%</span>
                                            </div>
                                            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-700 ${pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                            <p className="text-[10px] text-slate-600 mt-1">{readyMods} of {totalMods} modules complete</p>
                                        </div>

                                        <button
                                            onClick={() => {
                                                onActivateClient(client!);
                                                onNavigate({ page: 'srm_command_center', params: { projectId: proj.id, clientId: client!.id, clientName: client!.name, purpose: 'agency' } });
                                            }}
                                            className="w-full py-2 bg-slate-800 hover:bg-blue-600/20 text-slate-400 hover:text-blue-400 border border-slate-700 hover:border-blue-500/40 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                                        >
                                            Open in SRM Command Center →
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientDetailPage;
